import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
  setStep,
} from "app/lib/store/slices/steps/loanTypesSteps";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import RadioButton from "app/components/common/buttons/RadioButton";
import SubTitle from "app/components/common/typography/SubTitle";
import InputField from "app/components/common/inputs/InputField";
import SelectField from "app/components/common/inputs/SelectField";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import FormGroup from "app/components/common/inputs/FormGroup";

import { FinancingTenureFormData } from "app/utils/types";
const allOptions = [
  { value: 3, label: "3 years" },
  { value: 4, label: "4 years" },
  { value: 5, label: "5 years" },
  { value: 6, label: "6 years" },
  { value: 7, label: "7 years" },
];

const balloonPaymentOptions = [
  { value: 0, label: "0%" },
  { value: 10, label: "10%" },
  { value: 20, label: "20%" },
  { value: 30, label: "30%" },
  { value: 40, label: "40%" },
  { value: 50, label: "50%" },
];

const FinancingTenure: React.FC = () => {
  const dispatch = useDispatch();
  const [loanTypeOptions, setLoanTypeOptions] = useState(allOptions);
  const [balloonOptions, setBalloonOptions] = useState(balloonPaymentOptions);
  const [isDisabled, setIsDisabled] = useState(false);
  const [carAge, setCarAge] = useState<number>(0);

  const { loanApplication, vehicleDetail, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const isUsedvehicleConditionType = vehicleDetail?.vehicleCondition === "used";

  const { year } = vehicleDetail || {};

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm<FinancingTenureFormData>({
    defaultValues: {
      loanTerm: 0,
      rateType: "fixed",
      balloonPayment: 0,
    },
  });
  const loanTerm = watch("loanTerm");
  const onSubmit = (data: FinancingTenureFormData) => {
    const { price } = data || {};
    const currentYear = new Date().getFullYear();
    const carAge = year ? currentYear - year : 0;

    const maxLoanAmount = carAge >= 8 ? 50000 : 100000;
    if (Number(data.loanAmount) > maxLoanAmount) {
      setError("loanAmount", {
        type: "manual",
        message: `Based on your selected vehicle age, your loan limit is set to $${maxLoanAmount.toLocaleString()}`,
      });
      return;
    }

    if (data.loanTerm === 0) {
      setError("loanTerm", {
        type: "manual",
        message: `Loan Term is required`,
      });
      return;
    }

    dispatch(
      addStepsData({
        ...remaingData,
        vehicleDetail: {
          ...vehicleDetail,
          price: Number(price),
        },
      })
    );

    dispatch(
      addStepsData({
        ...remaingData,
        loanApplication: {
          ...loanApplication,
          rateType: data.rateType,
          loanAmount: Number(data.loanAmount),
          loanTerm: Number(data?.loanTerm),
          refinanceAmount: Number(data?.refinanceAmount),
          balloonPayment: data.balloonPayment,
        },
      })
    );
    dispatch(nextStep());
  };

  useEffect(() => {
    if (!year) return;

    const currentYear = new Date().getFullYear();
    const carAge = currentYear - year;
    setCarAge(carAge);

    if (carAge < 8 || carAge > 12) {
      setLoanTypeOptions(allOptions);
      setIsDisabled(false);
      return;
    }

    const newLoanTerm = 15 - carAge;
    const shouldLimitToMax5Years = carAge <= 10;

    let filteredOptions;
    if (newLoanTerm === 4) {
      filteredOptions = allOptions.filter((option) => option.value <= 4);
    } else if (shouldLimitToMax5Years) {
      filteredOptions = allOptions.filter((option) => option.value <= 5);
    } else {
      filteredOptions = allOptions.filter(
        (option) => option.value === newLoanTerm
      );
    }

    setLoanTypeOptions(filteredOptions);
    setIsDisabled(newLoanTerm !== 4 && !shouldLimitToMax5Years);
    setValue("loanTerm", newLoanTerm);
  }, [year, setValue]);

  useEffect(() => {
    if (loanApplication && Object.keys(loanApplication).length > 0) {
      if (loanApplication.rateType) {
        setValue("rateType", loanApplication.rateType);
      }
      if (loanApplication.loanAmount) {
        setValue("loanAmount", Number(loanApplication.loanAmount));
      }
      if (loanApplication.loanTerm) {
        setValue("loanTerm", Number(loanApplication.loanTerm));
      }
      if (loanApplication.refinanceAmount) {
        setValue("refinanceAmount", Number(loanApplication.refinanceAmount));
      }

      if (loanApplication.balloonPayment !== undefined) {
        setValue("balloonPayment", loanApplication.balloonPayment);
      }
      if (vehicleDetail?.price) {
        setValue("price", Number(vehicleDetail.price));
      }
    }
  }, [loanApplication, setValue]);

  useEffect(() => {
    if (loanTerm) {
      if (loanTerm === 3) {
        if (isUsedvehicleConditionType) {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
            { value: 30, label: "30%" },
            { value: 40, label: "40%" },
          ]);
        } else {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
            { value: 30, label: "30%" },
            { value: 40, label: "40%" },
            { value: 50, label: "50%" },
          ]);
        }
      } else if (loanTerm === 4) {
        if (isUsedvehicleConditionType) {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
            { value: 30, label: "30%" },
          ]);
        } else {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
            { value: 30, label: "30%" },
            { value: 40, label: "40%" },
          ]);
        }
      } else if (loanTerm === 5) {
        if (isUsedvehicleConditionType) {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
          ]);
        } else {
          setBalloonOptions([
            { value: 0, label: "0%" },
            { value: 10, label: "10%" },
            { value: 20, label: "20%" },
            { value: 30, label: "30%" },
          ]);
        }
      } else {
        setBalloonOptions(balloonPaymentOptions);
      }
    }
  }, [loanTerm]);

  const handleBack = () => {
    if (vehicleDetail?.isCarManuallyModel) {
      dispatch(setStep(3));
    } else {
      dispatch(prevStep());
    }
  };

  return (
    <>
      <ProgressBar progress={40} />
      <div className="w-full  sm:w-[70%] xl:w-[40%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Financing Tenure" />
        <SubTitle title="Please provide the details below to continue" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={`text-left  ${
              errors.rateType ? "mb-7" : "mb-4 sm:mb-5"
            }`}
          >
            <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-4">
              Please confirm rate type
            </h6>
            <div className="flex gap-5">
              <Controller
                name="rateType"
                control={control}
                rules={{ required: "Please select rate type" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-4 sm:gap-5">
                    <RadioButton
                      id="Fixed"
                      name="rateType"
                      value="fixed"
                      label="Fixed"
                      checked={value === "fixed"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="Variable"
                      name="rateType"
                      value="variable"
                      label="Variable"
                      checked={value === "variable"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>

            {errors.rateType && (
              <ErrorsMessage title={errors.rateType.message} />
            )}
          </div>
          <FormGroup>
            <InputField
              label="Purchase Price"
              name="price"
              control={control}
              type="number"
              required={true}
              placeholder="$30,000"
              error={errors.price?.message}
              className="flex-1"
              rules={{
                min: {
                  value: 1,
                  message: "value must be greater than 0",
                },
              }}
            />
            <InputField
              label="Loan amount"
              name="loanAmount"
              control={control}
              type="number"
              required={true}
              placeholder="$20,000"
              error={errors.loanAmount?.message}
              className="flex-1"
              rules={{
                min: {
                  value: 10000,
                  message: "Minimum loan amount is $10,000",
                },
                validate: (value: number) => {
                  const price = watch("price");
                  if (price && Number(value) > Number(price)) {
                    return "Loan amount cannot be higher than the purchase price";
                  }
                  return true;
                },
              }}
            />
          </FormGroup>
          <FormGroup>
            <SelectField
              label="Loan terms"
              name="loanTerm"
              control={control}
              options={loanTypeOptions}
              placeholder="3 years"
              error={errors.loanTerm?.message}
              required={true}
              isDisabled={isDisabled}
              className="flex-1"
            />

            {vehicleDetail?.purchaseFrom === "Refinance" && (
              <InputField
                label="Refinance amount"
                name="refinanceAmount"
                control={control}
                type="number"
                required={true}
                placeholder="$18,000"
                error={errors.refinanceAmount?.message}
                className="w-full sm:w-[49%]"
                rules={{
                  min: {
                    value: 1,
                    message: "value must be greater than 0",
                  },
                  validate: (value: number) => {
                    const loanAmount = watch("loanAmount");
                    if (loanAmount && Number(value) > Number(loanAmount)) {
                      return "Refinance amount cannot be higher than the loan price";
                    }
                    return true;
                  },
                }}
              />
            )}
            {vehicleDetail?.purchaseFrom !== "Refinance" && loanTerm <= 5 && (
              <SelectField
                label="Balloon payments"
                name="balloonPayment"
                control={control}
                options={balloonOptions}
                placeholder="20%"
                error={errors.balloonPayment?.message}
                required={true}
                className="w-full sm:w-[49%]"
                menuPlacement="top"
              />
            )}
          </FormGroup>

          <FormGroup>
            {vehicleDetail?.purchaseFrom === "Refinance" && loanTerm <= 5 && (
              <SelectField
                label="Balloon payments"
                name="balloonPayment"
                control={control}
                options={balloonOptions}
                placeholder="20%"
                error={errors.balloonPayment?.message}
                required={true}
                className="w-full sm:w-[49%]"
                menuPlacement="top"
              />
            )}
          </FormGroup>

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={handleBack}>Previous</PreviousButtom>
            <ContinueButton type="submit">Next</ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default FinancingTenure;
